import { useEffect } from 'react';

import { useTradingSessionStore } from '@/features/tradingSession/store';

import { useCustodialPanelState } from './useCustodialPanelState';

export {
    DEPOSIT_WALLET_REQUIREMENT,
    WITHDRAW_WALLET_REQUIREMENT,
} from './custodialWalletRequirements';

/**
 * The reopen half of the deposit/withdraw wallet gate.
 *
 * {@link useCustodialPanelState.openDeposit}/`openWithdraw` divert to the
 * Connected Wallet window when the session is not a wallet one (a passkey or
 * social user has no owner wallet), leaving `reopenAfterWalletAuthority` set.
 * This effect finishes the round trip: once that window mints a wallet
 * authority, the panel opens on the tab the user first asked for (prefill
 * kept). A cancelled window leaves the panel closed; nothing loops.
 */
export function useCustodialWalletRequirement(): void {
    const reopenPending = useCustodialPanelState(
        (s) => s.reopenAfterWalletAuthority,
    );
    const reauthenticationOpen = useTradingSessionStore(
        (s) => s.reauthenticationOpen,
    );
    const status = useTradingSessionStore((s) => s.status);
    const clockNowMs = useTradingSessionStore((s) => s.clockNowMs);

    const hasWalletAuthority =
        status?.state === 'active' &&
        status.method === 'eip712' &&
        useTradingSessionStore.getState().hasActiveAuthority(clockNowMs);

    // the falling edge of the wallet window: applyStatus runs before it closes,
    // so a granted wallet authority is already visible here.
    useEffect(() => {
        if (!reopenPending || reauthenticationOpen) return;
        const panel = useCustodialPanelState.getState();
        panel.clearReopenAfterWalletAuthority();
        if (!hasWalletAuthority || panel.isOpen) return;
        panel.reopenDiverted(
            reopenPending.tab,
            reopenPending.prefillAmount,
        );
    }, [hasWalletAuthority, reauthenticationOpen, reopenPending]);
}
